/** Copyright (c) 2025 DAIA Tech Pvt Ltd. All rights reserved.
 *
 * This software is confidential and proprietary information of DAIA Tech Pvt Ltd
 * and its licensors. You shall not disclose such Confidential Information and shall
 * use it only in accordance with the terms of the license agreement.
 *
 * Unauthorized reproduction, modification, distribution, or disclosure of this
 * software is strictly prohibited.
 */

import React from 'react';
import { Box, HStack, VStack, Separator, Flex } from 'chakra-v3';
import { Text } from '../../components/Text';

interface FeatureCardProps {
  number: string;
  boldText: string;
  regularText: string;
  bgColor: '#f3fcf8' | '#f9fcf3';
}

const FeatureCard: React.FC<FeatureCardProps> = ({ number, boldText, regularText, bgColor }) => (
  <Box
    bg={bgColor}
    border="0.8px solid"
    borderColor="rgba(10, 49, 60, 0.1)"
    borderRadius="10px"
    p={{ base: '16px', sm: '18px', md: '18px', lg: '20px', xl: '20px' }}
    w={{ base: '100%', md: '100%', lg: '100%', xl: '1120px', '2xl': '1120px' }}
    maxW={{ base: '100%', md: '100%', lg: '100%', xl: '1120px', '2xl': '1120px' }}
    mx="auto"
  >
    {/* Mobile and Tablet: Vertical Layout */}
    <VStack
      display={{ base: 'flex', md: 'none' }}
      gap={{ base: '12px', md: '16px' }}
      alignItems="flex-start"
      w="100%"
    >
      {/* Number */}
      <Text
        fontFamily="heading"
        fontSize={{ base: '18px', md: '22px' }}
        fontWeight="300"
        lineHeight="normal"
        letterSpacing="-1px"
        color="#0a313c"
      >
        {number}
      </Text>

      {/* Text Content */}
      <VStack gap="8px" alignItems="flex-start" w="100%">
        <Text
          fontFamily="'Noto Serif', serif"
          fontSize={{ base: '20px', md: '22px', lg: '24px' }}
          fontWeight="500"
          lineHeight="normal"
          letterSpacing="-1px"
          color="#0a313c"
          w="100%"
        >
          {boldText}
        </Text>
        <Text
          fontFamily="'Noto Serif', serif"
          fontSize={{ base: '20px', md: '22px', lg: '24px' }}
          fontWeight="300"
          lineHeight="normal"
          letterSpacing="-1px"
          color="#0a313c"
          w="100%"
        >
          {regularText}
        </Text>
      </VStack>
    </VStack>

    {/* Laptop and Desktop: Horizontal Layout */}
    <HStack
      display={{ base: 'none', md: 'flex' }}
      gap="24px"
      alignItems="center"
      w="100%"
      flexWrap="wrap"
    >
      {/* Number */}
      <Text
        fontFamily="heading"
        fontSize={{ md: '22px', lg: '22px', xl: '24px' }}
        fontWeight="300"
        lineHeight="normal"
        letterSpacing="-1px"
        color="#0a313c"
        flexShrink={0}
      >
        {number}
      </Text>

      {/* Vertical Separator */}
      <Separator
        orientation="vertical"
        flexShrink={0}
        alignSelf="stretch"
        borderColor="rgba(10, 49, 60, 0.2)"
        display={{ base: 'none', md: 'flex' }}
      />

      {/* Text Content */}
      <Flex
        gap="8px"
        alignItems="flex-start"
        w={{ base: '100%', md: '100%', lg: '910px', xl: '910px' }}
        minW={0}
        flexWrap="wrap"
        flex="1"
      >
        <Text
          fontFamily="'Noto Serif', serif"
          fontSize={{ md: '22px', lg: '24px', xl: '24px' }}
          fontWeight="500"
          lineHeight="normal"
          letterSpacing="-1px"
          color="#0a313c"
          flexShrink={0}
        >
          {boldText}
        </Text>
        <Text
          fontFamily="'Noto Serif', serif"
          fontSize={{ md: '22px', lg: '24px', xl: '24px' }}
          fontWeight="300"
          lineHeight="normal"
          letterSpacing="-1px"
          color="#0a313c"
          flex="1"
          minW={0}
        >
          {regularText}
        </Text>
      </Flex>
    </HStack>
  </Box>
);

const WhatMakesSamikshaDistinct: React.FC = () => {
  const featureCards = [
    {
      number: '01',
      boldText: 'Human-centered evaluation',
      regularText: 'captures culturally nuanced failures automated systems miss.',
      bgColor: '#f3fcf8' as const,
    },
    {
      number: '02',
      boldText: 'Multi-layered, domain-specific queries',
      regularText: 'faced by low-resource/income communities in India.',
      bgColor: '#f9fcf3' as const,
    },
    {
      number: '03',
      boldText: 'Demonstrates Samiksha in action',
      regularText: 'avoiding ceiling effects and false positives.',
      bgColor: '#f3fcf8' as const,
    },
    {
      number: '04',
      boldText: 'Ethical labor + Fair wages',
      regularText: 'faced by low-resource/income communities in India.',
      bgColor: '#f9fcf3' as const,
    },
  ];

  return (
    <Box
      w="100%"
      pt={0}
      pb={{ base: '32px', sm: '40px', md: '50px', lg: '60px', xl: '60px' }}
      px={{ base: 4, sm: 6, md: 6 }}
      bg="#F7F3EF"
    >
      <VStack
        gap={{ base: '32px', sm: '40px', md: '56px', lg: '72px', xl: '72px' }}
        alignItems="center"
        w="100%"
        maxW={{ base: '100%', md: '1200px', lg: '1200px', xl: '1200px', '2xl': '1200px' }}
        mx="auto"
      >
        {/* Main Heading - Centered */}
        <Text
          fontFamily="heading"
          fontSize="40px"
          fontWeight="300"
          lineHeight="normal"
          letterSpacing="-1px"
          color="#0A313C"
          textAlign="center"
          alignSelf="stretch"
          w="100%"
        >
          What Makes Samiksha Distinct
        </Text>

        {/* Feature Cards */}
        <VStack gap={{ base: '20px', sm: '24px', md: '28px', lg: '36px', xl: '36px' }} alignItems="flex-start" w="100%">
          {featureCards.map((card) => (
            <FeatureCard
              key={card.number}
              number={card.number}
              boldText={card.boldText}
              regularText={card.regularText}
              bgColor={card.bgColor}
            />
          ))}
        </VStack>
      </VStack>
    </Box>
  );
};

export default WhatMakesSamikshaDistinct;
